import { DateTime } from "luxon";
import type { ReportMoney } from "api/owner-screening-report";
import { RECOMMENDATION_LABELS, type FieldFormat } from "./constants";

/**
 * Every formatter returns null rather than a placeholder when the figure is
 * absent. A key the server left out means that report produced no number, which
 * is not the same as zero — the caller drops the row instead of printing a dash
 * an owner would read as "none".
 */
const formatDollars = (
  amount?: number | null,
  currency = "USD"
): string | null => {
  if (typeof amount !== "number" || Number.isNaN(amount)) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatMoney = (money?: ReportMoney | null): string | null => {
  if (!money || typeof money.cents !== "number") return null;
  return formatDollars(money.cents / 100, money.currency || "USD");
};

export const formatDate = (iso?: string | null): string | null =>
  iso ? DateTime.fromISO(iso).toFormat("MMM d, yyyy") : null;

const formatNumber = (value?: number | null): string | null =>
  typeof value === "number"
    ? new Intl.NumberFormat("en-US").format(value)
    : null;

/** Income to rent reads as a multiple of the rent: 3.2x. */
const formatRatio = (value?: number | null): string | null =>
  typeof value === "number" ? `${value}x` : null;

const formatPercent = (value?: number | null): string | null =>
  typeof value === "number" ? `${value}%` : null;

/**
 * Counts an owner reads as a phrase rather than a bare number — "0 convictions"
 * says what the zero is about, where a lone 0 in a Criminal card does not.
 */
const formatCount = (
  value: number | null | undefined,
  one: string,
  many: string
): string | null => {
  if (typeof value !== "number") return null;
  return `${value} ${value === 1 ? one : many}`;
};

/**
 * Presence checks, where zero is the answer rather than a quantity: the design
 * reads them as "No record" / "No match" and only counts when there is one.
 */
const formatPresence = (
  value: number | null | undefined,
  none: string,
  one: string,
  many: string
): string | null => {
  if (typeof value !== "number") return null;
  if (value === 0) return none;
  return `${value} ${value === 1 ? one : many}`;
};

const formatMonths = (value?: number | null): string | null => {
  if (typeof value !== "number") return null;
  return value === 1 ? "1 month" : `${value} months`;
};

/**
 * Renders one figure from the payload according to its declared format. Returns
 * null when the figure is absent, which is how a row or a card is dropped.
 */
export const formatField = (
  value: unknown,
  format: FieldFormat
): string | null => {
  switch (format) {
    case "money":
      return typeof value === "number"
        ? formatDollars(value)
        : formatMoney(value as ReportMoney);
    case "ratio":
      return formatRatio(value as number);
    case "percent":
      return formatPercent(value as number);
    case "months":
      return formatMonths(value as number);
    case "convictions":
      return formatCount(value as number, "conviction", "convictions");
    case "records":
      return formatPresence(value as number, "No record", "record", "records");
    case "matches":
      return formatPresence(value as number, "No match", "match", "matches");
    case "date":
      return formatDate(value as string);
    case "recommendation":
      return typeof value === "string"
        ? (RECOMMENDATION_LABELS[value] ?? null)
        : null;
    default:
      return formatNumber(value as number);
  }
};
